import {
	faChevronDown,
	faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useState, type ReactElement } from 'react';
import { Icon } from '../../components/base/Icon';
import { Input } from '../../components/forms/Input';
import { SingleSelect } from '../../components/forms/SingleSelect';
import { TextArea } from '../../components/forms/TextArea';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import {
	ANTHROPIC_MODEL_OPTIONS,
	DEFAULT_TASK_MODEL_OPTION,
} from '../constants/llmModelOptions';
import type { AvailableDataSource } from './DynamicFieldValue';
import { DynamicFieldValue } from './DynamicFieldValue';

/**
 * Configuration form for the `email_ingest` step, presented as "Read Email".
 *
 * The step type key stays `email_ingest` (server registry, config keys,
 * validation); only the presentation is generic. The form is deliberately
 * small: which email to read, what to determine from it, and the limits
 * applied before anything is sent to the model.
 */

export interface EmailIngestAttachmentPolicy {
	mimeAllowlist?: Array<string>;
	maxAttachments?: number;
	maxBytesPerAttachment?: number;
	maxTotalBytes?: number;
	maxPdfPages?: number;
}

export interface EmailIngestStepConfigProps {
	config: {
		emailRecordId?: string;
		outcomeType?: string;
		includeBody?: boolean;
		attachmentPolicy?: EmailIngestAttachmentPolicy;
		model?: string;
		language?: string;
	};
	setConfig: (config: EmailIngestStepConfigProps['config']) => void;
	getAvailableDataSources?: () => Array<AvailableDataSource>;
	getDisplayText?: (value: string) => string;
}

/**
 * Only one outcome exists today. It is rendered as a select anyway so adding
 * the next one is a data change here rather than a new control.
 */
const OUTCOME_TYPE_OPTIONS = [
	{ value: 'order_sheet', label: 'Find a product request' },
];

/**
 * Mirrors `DEFAULT_ATTACHMENT_POLICY` on the server
 * (`apps/crm-api/src/modules/email-ingest/services/email-message-reader.service.ts`).
 * The step falls back to these when a value is absent, so the form shows them
 * as the effective settings rather than leaving the fields blank.
 */
const DEFAULT_POLICY: Required<EmailIngestAttachmentPolicy> = {
	mimeAllowlist: [
		'application/pdf',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'application/vnd.ms-excel',
		'text/csv',
		'text/plain',
		'image/png',
		'image/jpeg',
		'image/gif',
		'image/webp',
	],
	maxAttachments: 10,
	maxBytesPerAttachment: 10485760,
	maxTotalBytes: 26214400,
	maxPdfPages: 50,
};

export function EmailIngestStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
}: EmailIngestStepConfigProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [policyExpanded, setPolicyExpanded] = useState(false);

	const policy = config.attachmentPolicy ?? {};

	const updateConfig = <K extends keyof EmailIngestStepConfigProps['config']>(
		key: K,
		value: EmailIngestStepConfigProps['config'][K],
	): void => {
		setConfig({ ...config, [key]: value });
	};

	const updatePolicy = <K extends keyof EmailIngestAttachmentPolicy>(
		key: K,
		value: EmailIngestAttachmentPolicy[K],
	): void => {
		setConfig({
			...config,
			attachmentPolicy: { ...policy, [key]: value },
		});
	};

	/** Number fields fall back to the server default when cleared. */
	const updatePolicyNumber = (
		key:
			| 'maxAttachments'
			| 'maxBytesPerAttachment'
			| 'maxTotalBytes'
			| 'maxPdfPages',
		raw: string,
	): void => {
		const parsed = parseInt(raw, 10);

		updatePolicy(key, isNaN(parsed) || parsed <= 0 ? undefined : parsed);
	};

	const mimeAllowlistValue = (
		policy.mimeAllowlist ?? DEFAULT_POLICY.mimeAllowlist
	).join('\n');

	/**
	 * An unset model means "whatever the server defaults to", which is the
	 * setting that keeps working when the server default moves — so it stays the
	 * first option and names the current default rather than showing blank.
	 */
	const modelOptions = [
		{
			value: '',
			label: `${t('Server default')} (${t(DEFAULT_TASK_MODEL_OPTION.label)})`,
		},
		{
			value: DEFAULT_TASK_MODEL_OPTION.value,
			label: t(DEFAULT_TASK_MODEL_OPTION.label),
		},
		...ANTHROPIC_MODEL_OPTIONS.map((option) => ({
			value: option.value,
			label: t(option.label),
		})),
	];

	return (
		<div className="space-y-6">
			<div>
				<h3
					className={`text-base font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('Read Email')}
				</h3>

				{/* Email record to read */}
				<div className="mb-4">
					<DynamicFieldValue
						label={t('Email Record ID')}
						value={config.emailRecordId ?? ''}
						onChange={(value) => {
							updateConfig('emailRecordId', value);
						}}
						dataType="string"
						required={true}
						placeholder={t('Select the Email Record ID')}
						description={t('Choose the Email Record ID to read.')}
						availableData={getAvailableDataSources?.() ?? []}
						displayTransform={getDisplayText}
					/>
				</div>

				{/* Outcome type */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('What to Determine')}
					</label>
					<SingleSelect
						name="email-ingest-outcome-type"
						value={config.outcomeType ?? 'order_sheet'}
						onChange={(value) => {
							updateConfig('outcomeType', value);
						}}
						options={OUTCOME_TYPE_OPTIONS.map((option) => ({
							value: option.value,
							label: t(option.label),
						}))}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('What to read out of the message.')}
					</p>
				</div>

				{/* Include message body */}
				<div className="mb-4">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={config.includeBody !== false}
							onChange={(e) => {
								updateConfig('includeBody', e.target.checked);
							}}
						/>
						<span
							className={`text-sm font-medium ${colors.classes.textPrimary}`}
						>
							{t('Read the message body')}
						</span>
					</label>
					<p className={`ml-6 text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Reads the body as well as the attachments. Links and remote images are removed first.',
						)}
					</p>
				</div>

				{/* Model. The step runs only through the Anthropic task service, so
				    there is no provider choice — Claude models only. */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Model')}
					</label>
					<SingleSelect
						name="email-ingest-model"
						value={config.model ?? ''}
						onChange={(value) => {
							updateConfig('model', value === '' ? undefined : value);
						}}
						options={modelOptions}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('The model used to read the message.')}
					</p>
				</div>

				{/* Optional source language hint */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Source Language')}
					</label>
					<Input
						value={config.language ?? ''}
						onChange={(e) => {
							updateConfig(
								'language',
								e.target.value === '' ? undefined : e.target.value,
							);
						}}
						placeholder={t('For example: German')}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('Optional hint about the language of the message.')}
					</p>
				</div>
			</div>

			{/* Attachment limits — advanced, collapsed by default */}
			<div
				className={`border rounded-lg ${colors.classes.borderDefault} overflow-hidden`}
			>
				<button
					type="button"
					onClick={() => {
						setPolicyExpanded(!policyExpanded);
					}}
					className={`w-full flex items-center gap-2 px-3 py-2.5 text-left ${colors.classes.bgSecondary}`}
				>
					<Icon
						icon={policyExpanded ? faChevronDown : faChevronRight}
						className={`text-xs ${colors.classes.textSecondary}`}
					/>
					<span className={`text-sm font-medium ${colors.classes.textPrimary}`}>
						{t('Attachment Limits')}
					</span>
				</button>

				{policyExpanded && (
					<div className="p-3 space-y-4">
						<p className={`text-xs ${colors.classes.textSecondary}`}>
							{t(
								'Applied before anything is read. Attachments over a limit are skipped and listed in the step warnings.',
							)}
						</p>

						{/* Allowed file types */}
						<div>
							<label
								className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
							>
								{t('Allowed File Types')}
							</label>
							<TextArea
								value={mimeAllowlistValue}
								onChange={(e) => {
									const entries = e.target.value
										.split('\n')
										.map((entry) => entry.trim())
										.filter((entry) => entry !== '');

									updatePolicy(
										'mimeAllowlist',
										entries.length > 0 ? entries : undefined,
									);
								}}
								rows={5}
								placeholder={t('One file type per line')}
							/>
							<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
								{t('One MIME type per line. Anything else is skipped.')}
							</p>
						</div>

						<div className="grid grid-cols-2 gap-4">
							{/* Max attachments */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Attachments')}
								</label>
								<Input
									type="number"
									value={policy.maxAttachments ?? DEFAULT_POLICY.maxAttachments}
									onChange={(e) => {
										updatePolicyNumber('maxAttachments', e.target.value);
									}}
									min={1}
								/>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t('Default 10.')}
								</p>
							</div>

							{/* Max PDF pages */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum PDF Pages')}
								</label>
								<Input
									type="number"
									value={policy.maxPdfPages ?? DEFAULT_POLICY.maxPdfPages}
									onChange={(e) => {
										updatePolicyNumber('maxPdfPages', e.target.value);
									}}
									min={1}
								/>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t('Longer PDFs are skipped. Default 50.')}
								</p>
							</div>

							{/* Per-attachment bytes */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Size Per Attachment')}
								</label>
								<Input
									type="number"
									value={
										policy.maxBytesPerAttachment ??
										DEFAULT_POLICY.maxBytesPerAttachment
									}
									onChange={(e) => {
										updatePolicyNumber('maxBytesPerAttachment', e.target.value);
									}}
									min={1}
								/>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t('In bytes. Default 10485760 (10 MB).')}
								</p>
							</div>

							{/* Total bytes */}
							<div>
								<label
									className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
								>
									{t('Maximum Size For All Attachments')}
								</label>
								<Input
									type="number"
									value={policy.maxTotalBytes ?? DEFAULT_POLICY.maxTotalBytes}
									onChange={(e) => {
										updatePolicyNumber('maxTotalBytes', e.target.value);
									}}
									min={1}
								/>
								<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
									{t('In bytes. Default 26214400 (25 MB).')}
								</p>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
